import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * Providers — lo que envuelve a toda pantalla React del portal.
 *
 * Por ahora solo TanStack Query. No hay proveedor de sesión ni de tema, y es
 * deliberado: durante la convivencia el dueño de las dos cosas sigue siendo el
 * shell vanilla —`Auth` guarda la sesión, `Theme` escribe `data-theme` en el
 * `<html>`— y duplicarlos daría dos verdades que acabarían discrepando. La capa
 * React los lee a través de `lib/`, no los reimplementa.
 */
export function Providers({ children }: { children: React.ReactNode }) {
  // En estado y no en un módulo: si el cliente viviera fuera del componente,
  // dos montajes seguidos compartirían la caché de la pantalla anterior.
  const [cliente] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // 30 s de frescura. El portal muestra datos de gestión —solicitudes,
            // fichas, cursos— que cambian en minutos, no en segundos: volver a
            // preguntar en cada montaje es lo que hacía que regresar a una
            // pantalla costase lo mismo que entrar la primera vez.
            staleTime: 30_000,
            // Al volver a la pestaña sí se revalida: es el momento en que el
            // usuario mira, y es más barato que el sondeo cada 60 s que hay hoy.
            refetchOnWindowFocus: true,
            // Un 403 no mejora por insistir, y reintentarlo tres veces retrasa
            // el mensaje que explica lo que pasa.
            retry: (intentos, error) => {
              const codigo = (error as { status?: number })?.status;
              if (codigo === 403 || codigo === 404 || codigo === 401) return false;
              return intentos < 2;
            },
          },
        },
      }),
  );

  return <QueryClientProvider client={cliente}>{children}</QueryClientProvider>;
}
